import React, { useMemo, useState } from 'react';
import { inventoryService } from '../../services/inventoryService';
import { workOrderService } from '../../services/workOrderService';
import { greenBeanService } from '../../services/greenBeanService';
import { profileService } from '../../services/profileService';
import { LoadingSpinner } from '../../components/common/LoadingSpinner';
import { Link } from 'react-router-dom';
import { Search, Paperclip, Eye } from 'lucide-react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { ProfileManager } from '../../components/inventory/ProfileManager';
import { QCStatusButton } from '../../components/inventory/QCStatusButton';
import { Modal } from '../../components/common/Modal';
import { GreenBeanProfileForm } from '../purchasing/GreenBeanProfileForm';
import { CuppingProfileForm } from '../inventory/CuppingProfileForm';
import { useLanguage } from '../../features/language/useLanguage';
import { usePermissions } from '../../hooks/usePermissions';
import { Pagination } from '../../components/common/Pagination';
import { LotRoastWorkOrderCell } from './components/LotRoastWorkOrderCell';
import { usePaginationState } from '../../hooks/usePaginationState';
import api from '../../services/api';

import type { CuppingProfile } from '../../services/profileService';
import type { GreenBeanProfile } from '../../types/greenBean';

export const LotList: React.FC = () => {
  const { t } = useLanguage();
  const { can } = usePermissions();
  const canWriteInventory = can('inventory', 'write');
  const readOnly = !canWriteInventory;
  // const [lots, setLots] = useState<InventoryLot[]>([]); // Removed
  // const [loading, setLoading] = useState(true); // Removed
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'depleted'>('active');
  const [selectedLotImage, setSelectedLotImage] = useState<{ lotId: string; lotNumber: string } | null>(null);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [previewBlobUrl, setPreviewBlobUrl] = useState<string | null>(null);
  // QC Modal State
  const [qcModal, setQcModal] = useState<{
    isOpen: boolean;
    type: 'bean' | 'cupping';
    entityId: string;
    entityType: 'LotType' | 'SampleType';
    lotId?: string;
  }>({
    isOpen: false,
    type: 'bean',
    entityId: '',
    entityType: 'LotType',
  });

  const { data: greenBeanProfilesData, refetch: refetchGreenBeans } = useQuery({
    queryKey: ['green-beans'],
    queryFn: greenBeanService.getAll,
  });

  const { data: cuppingProfilesData, refetch: refetchCupping } = useQuery({
    queryKey: ['cupping-profiles'],
    queryFn: profileService.getAllCuppingProfiles,
  });

  const greenBeanProfiles = greenBeanProfilesData || [];
  const cuppingProfiles = cuppingProfilesData || [];

  const { page, size, setPage, setSize, resetOnChange } = usePaginationState({ defaultSize: 25 });
  resetOnChange([searchTerm, statusFilter]);

  const { data: lotsData, isLoading: loading, refetch: fetchLots } = useQuery({
    queryKey: ['lots', { page, size, search: searchTerm, status: statusFilter }],
    queryFn: () => inventoryService.getAllLots({
      limit: size,
      offset: (page - 1) * size,
      search: searchTerm.trim() || undefined,
      status: statusFilter === 'all' ? undefined : statusFilter,
    }),
    placeholderData: keepPreviousData,
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 30,
  });

  const lots = lotsData?.data || [];
  const total = lotsData?.total ?? 0;

  const lotIds = useMemo(() => lotsData?.data?.map((l) => l.id) ?? [], [lotsData]);

  const { data: woByLot, isLoading: isWoLoading } = useQuery({
    queryKey: ['work-orders-completed-by-lots', lotIds],
    queryFn: () => workOrderService.getCompletedByLots(lotIds),
    enabled: lotIds.length > 0,
    staleTime: 1000 * 60,
    placeholderData: keepPreviousData,
  });

  const getGreenBeanQC = (entityId: string) => {
    const profile = greenBeanProfiles.find(p => p.lot_id === entityId);
    return profile?.sample_physical_check;
  };


  const handleQCClick = (type: 'bean' | 'cupping', entityId: string, entityType: 'LotType' | 'SampleType', lotId?: string) => {
    setQcModal({
      isOpen: true,
      type,
      entityId,
      entityType,
      lotId,
    });
  };

  const getInitialQCData = () => {
    const { type, entityId, entityType } = qcModal;
    if (type === 'bean') {
      return entityType === 'LotType' 
        ? greenBeanProfiles.find(p => p.lot_id === entityId)
        : greenBeanProfiles.find(p => p.sample_id === entityId);
    } else {
      return entityType === 'LotType'
        ? cuppingProfiles.find(p => p.lot_id === entityId)
        : cuppingProfiles.find(p => p.sample_id === entityId);
    }
  };

  const openLotImagePreview = async (lotId: string, lotNumber: string) => {
    if (previewBlobUrl) {
      URL.revokeObjectURL(previewBlobUrl);
      setPreviewBlobUrl(null);
    }
    setSelectedLotImage({ lotId, lotNumber });
    setIsImageModalOpen(true);
    try {
      const response = await api.get(`/uploads/inventory/${lotId}/image`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([response.data]));
      setPreviewBlobUrl(url);
    } catch (err) {
      console.error('Failed to fetch lot image preview', err);
    }
  };

  React.useEffect(() => {
    return () => {
      if (previewBlobUrl) {
        URL.revokeObjectURL(previewBlobUrl);
      }
    };
  }, [previewBlobUrl]);

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <h1 className="text-2xl font-semibold text-gray-900">{t('purchasing.lotList.title')}</h1>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row items-end gap-4 bg-white p-4 rounded-lg shadow-sm border border-gray-200">
        <div className="relative flex-1 w-full">
          <label htmlFor="search" className="block text-sm font-medium text-gray-700 mb-1">{t('purchasing.lotList.filters.search')}</label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-gray-400" aria-hidden="true" />
            </div>
            <input
              id="search"
              type="text"
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
              placeholder={t('purchasing.lotList.filters.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
        <div className="w-full sm:w-48">
          <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-1">{t('purchasing.lotList.filters.status')}</label>
          <select
            id="status"
            className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md border"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as 'all' | 'active' | 'depleted')}
          >
            <option value="all">{t('purchasing.lotList.status.all')}</option>
            <option value="active">{t('purchasing.lotList.status.active')}</option>
            <option value="depleted">{t('purchasing.lotList.status.depleted')}</option>
          </select>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden" data-test-id="lot-list-table-container">
        {loading ? (
          <LoadingSpinner text={t('purchasing.lotList.loading')} />
        ) : lots.length === 0 ? (
          <div className="p-8 text-center italic text-gray-500">{t('purchasing.lotList.empty')}</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('purchasing.lotList.table.lotNumber')}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('purchasing.lotList.table.item')}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('purchasing.lotList.table.source')}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('purchasing.lotList.table.receivedDate')}</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">{t('purchasing.lotList.table.remaining')}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('purchasing.lotList.table.status')}</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">{t('purchasing.lotList.table.greenBeanProfile')}</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">{t('purchasing.lotList.table.roastProfile')}</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">{t('purchasing.lotList.table.tasteProfile')}</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">{t('purchasing.lotList.table.qualityControl')}</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">{t('purchasing.lotList.table.actions')}</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {lots
                  .filter(lot => lot.sourceType !== 'work_order')
                  .map((lot) => {
                  return (
                    <tr key={lot.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        <div className="flex items-center gap-2">
                          <Link to={`/inventory/lots/${lot.id}`} className="text-primary-600 hover:text-primary-900 hover:underline">
                            {lot.lotNumber}
                          </Link>
                          {(lot.photoUrl || lot.photoUploadedAt || lot.photoName) && (
                            <button
                              type="button"
                              onClick={() => openLotImagePreview(lot.id, lot.lotNumber)}
                              className="group relative text-primary-500 hover:text-primary-700"
                              title={t('purchasing.lotList.actions.viewPhoto')}
                              aria-label={t('purchasing.lotList.actions.viewPhoto')}
                            >
                              <Paperclip className="h-4 w-4" />
                              <span className="absolute left-full ml-2 top-1/2 -translate-y-1/2 whitespace-nowrap rounded bg-gray-800 px-2 py-1 text-[10px] text-white opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all">
                                {t('purchasing.lotList.actions.viewPhoto')}
                              </span>
                            </button>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <div className="font-medium text-gray-900">{lot.itemName || t('purchasing.lotList.values.unknownItem')}</div>
                        <div className="text-gray-500 text-xs">{lot.itemCode || lot.inventoryItemId}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                         {lot.sourceRef ? (
                            <Link to={lot.sourceType === 'purchase_order' ? `/purchase-orders` : '#'} className="text-primary-600 hover:underline">
                              {lot.sourceRef}
                            </Link>
                         ) : '-'}
                         <div className="text-xs text-gray-400 capitalize">{lot.sourceType?.replace('_', ' ')}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(lot.receivedDate).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">{lot.remainingQuantity}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          lot.remainingQuantity > 0 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {lot.remainingQuantity > 0 ? t('purchasing.lotList.status.active') : t('purchasing.lotList.status.depleted')}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-gray-500 text-center">
                        <ProfileManager
                          entityType="LotType"
                          entityId={lot.id}
                          profileType="greenBean"
                          existingProfile={greenBeanProfiles.find(p => p.lot_id === lot.id)}
                          onProfileUpdate={() => fetchLots()}
                        />
                      </td>
                      <td className="px-6 py-4 text-gray-500 text-center">
                        <LotRoastWorkOrderCell
                          lotId={lot.id}
                          workOrders={woByLot?.[lot.id] ?? []}
                          readOnly={readOnly}
                          isLoading={isWoLoading}
                        />
                      </td>
                      <td className="px-6 py-4 text-gray-500 text-center">
                        <ProfileManager
                          entityType="LotType"
                          entityId={lot.id}
                          profileType="cupping"
                          onProfileUpdate={() => fetchLots()}
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center space-x-2">
                        <QCStatusButton
                          type="Bean"
                          status={getGreenBeanQC(lot.id)}
                          onClick={() => handleQCClick('bean', lot.id, 'LotType')}
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <Link to={`/inventory/lots/${lot.id}`} className="text-primary-600 hover:text-primary-900">
                          <Eye />
                        </Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <Pagination page={page} pageSize={size} total={total} onPageChange={setPage} onPageSizeChange={setSize} isLoading={loading} />
      </div>

      {/* QC Modal */}
      {qcModal.isOpen && (
        <Modal
          isOpen={qcModal.isOpen}
          onClose={() => setQcModal(prev => ({ ...prev, isOpen: false }))}
          title={qcModal.type === 'bean' ? t('purchasing.lotList.modal.greenBeanQc') : t('purchasing.lotList.modal.cuppingQc')}
          maxWidth={qcModal.type === 'bean' ? '4xl' : '6xl'}
        >
          {qcModal.type === 'bean' ? (
            <GreenBeanProfileForm
              entityType={qcModal.entityType}
              entityId={qcModal.entityId}
              initialData={getInitialQCData() as Partial<GreenBeanProfile>}
              onSuccess={() => {
                setQcModal(prev => ({ ...prev, isOpen: false }));
                fetchLots();
                refetchGreenBeans();
              }}
              onCancel={() => setQcModal(prev => ({ ...prev, isOpen: false }))}
            />
          ) : (
            <CuppingProfileForm
              entityType={qcModal.entityType}
              entityId={qcModal.entityId}
              lotId={qcModal.lotId}
              initialData={getInitialQCData() as Partial<CuppingProfile>}
              onSuccess={() => {
                setQcModal(prev => ({ ...prev, isOpen: false }));
                fetchLots();
                refetchCupping();
              }}
              onCancel={() => setQcModal(prev => ({ ...prev, isOpen: false }))}
            />
          )}
        </Modal>
      )}

      <Modal
        isOpen={isImageModalOpen}
        onClose={() => {
          setIsImageModalOpen(false);
          setSelectedLotImage(null);
        }}
        title={t('purchasing.lotList.modal.lotPhoto')}
        maxWidth="4xl"
      >
        <div className="flex min-h-[40vh] items-center justify-center rounded-lg bg-gray-50 p-4">
          {previewBlobUrl ? (
            <img
              src={previewBlobUrl}
              alt={selectedLotImage ? `${t('purchasing.lotList.modal.lotPhoto')} ${selectedLotImage.lotNumber}` : t('purchasing.lotList.modal.lotPhoto')}
              className="max-h-[70vh] max-w-full rounded-md object-contain shadow-md"
            />
          ) : (
            <div className="text-sm italic text-gray-500">{t('inventory.lotDetail.loadingShort')}</div>
          )}
        </div>
      </Modal>
    </div>
  );
};
